import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isProduction, API_BASE } from '../utils/storageMode';
import { uploadImageFile } from '../utils/uploadImage';

const PortfolioContext = createContext(null);

// Default portfolio data (used for localStorage mode)
// Empty by default - add projects via the admin dashboard
const defaultProjects = [];

export const PortfolioProvider = ({ children }) => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [storageMode, setStorageMode] = useState('local'); // 'local' or 'api'

    // Determine storage mode on mount
    useEffect(() => {
        const mode = isProduction() ? 'api' : 'local';
        setStorageMode(mode);
        console.log(`Portfolio storage mode: ${mode}`);
    }, []);

    // Load projects based on storage mode
    useEffect(() => {
        const loadProjects = async () => {
            setIsLoading(true);
            setError(null);

            try {
                if (storageMode === 'api') {
                    // Production: Load from PHP API
                    const response = await fetch(`${API_BASE}/portfolio.php`, {
                        credentials: 'include'
                    });
                    const data = await response.json();
                    
                    if (data.success) {
                        setProjects(data.projects);
                    } else {
                        throw new Error(data.error || 'Failed to load projects');
                    }
                } else {
                    // Development/Prototype: Load from localStorage
                    const savedProjects = localStorage.getItem('knitster_portfolio');
                    if (savedProjects) {
                        const parsed = JSON.parse(savedProjects);
                        setProjects(Array.isArray(parsed) ? parsed : []);
                    } else {
                        setProjects(defaultProjects);
                    }
                }
            } catch (err) {
                console.error('Error loading projects:', err);
                setError(err.message);
                // Fallback to defaults
                setProjects(defaultProjects);
            } finally {
                setIsLoading(false);
            }
        };

        if (storageMode) {
            loadProjects();
        }
    }, [storageMode]);

    // Save to localStorage when projects change (only in local mode)
    useEffect(() => {
        if (!isLoading && storageMode === 'local' && projects.length > 0) {
            localStorage.setItem('knitster_portfolio', JSON.stringify(projects));
        }
    }, [projects, isLoading, storageMode]);

    // Upload image - handles both modes
    const uploadImage = useCallback((file) => uploadImageFile(file, storageMode), [storageMode]);

    // Add a new project
    const addProject = useCallback(async (project) => {
        try {
            if (storageMode === 'api') {
                const response = await fetch(`${API_BASE}/portfolio.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(project)
                });
                
                const data = await response.json();
                
                if (data.success) {
                    setProjects(data.projects);
                    return data.project;
                } else {
                    throw new Error(data.error);
                }
            } else {
                const newProject = {
                    ...project,
                    id: Date.now(),
                    images: project.images || []
                };
                setProjects(prev => [newProject, ...prev]);
                return newProject;
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [storageMode]);

    // Update an existing project
    const updateProject = useCallback(async (id, updates) => {
        try {
            if (storageMode === 'api') {
                const response = await fetch(`${API_BASE}/portfolio.php?id=${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(updates)
                });
                
                const data = await response.json();
                
                if (data.success) {
                    setProjects(data.projects);
                } else {
                    throw new Error(data.error);
                }
            } else {
                setProjects(prev => prev.map(p =>
                    p.id === id ? { ...p, ...updates } : p
                ));
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [storageMode]);

    // Delete a project
    const deleteProject = useCallback(async (id) => {
        try {
            if (storageMode === 'api') {
                const response = await fetch(`${API_BASE}/portfolio.php?id=${id}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                
                const data = await response.json();
                
                if (data.success) {
                    setProjects(data.projects);
                } else {
                    throw new Error(data.error);
                }
            } else {
                setProjects(prev => prev.filter(p => p.id !== id));
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [storageMode]);

    // Reorder projects
    const reorderProject = useCallback(async (id, direction) => {
        try {
            if (storageMode === 'api') {
                const response = await fetch(`${API_BASE}/portfolio.php?action=reorder`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ id, direction })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    setProjects(data.projects);
                }
            } else {
                const index = projects.findIndex(p => p.id === id);
                if (index === -1) return;

                const newIndex = direction === 'up' ? index - 1 : index + 1;
                if (newIndex < 0 || newIndex >= projects.length) return;

                const newProjects = [...projects];
                const [removed] = newProjects.splice(index, 1);
                newProjects.splice(newIndex, 0, removed);
                setProjects(newProjects);
            }
        } catch (err) {
            setError(err.message);
        }
    }, [storageMode, projects]);

    // Reset to defaults
    const resetToDefaults = useCallback(async () => {
        try {
            if (storageMode === 'api') {
                const response = await fetch(`${API_BASE}/portfolio.php?action=reset`, {
                    method: 'POST',
                    credentials: 'include'
                });
                
                const data = await response.json();
                
                if (data.success) {
                    setProjects(data.projects);
                }
            } else {
                setProjects(defaultProjects);
                localStorage.setItem('knitster_portfolio', JSON.stringify(defaultProjects));
            }
        } catch (err) {
            setError(err.message);
        }
    }, [storageMode]);

    // Legacy methods for backwards compatibility
    const addImageToProject = useCallback((projectId, imageUrl) => {
        setProjects(prev => prev.map(p =>
            p.id === projectId
                ? { ...p, images: [...(p.images || []), imageUrl] }
                : p
        ));
    }, []);

    const removeImageFromProject = useCallback((projectId, imageIndex) => {
        setProjects(prev => prev.map(p =>
            p.id === projectId
                ? { ...p, images: p.images.filter((_, i) => i !== imageIndex) }
                : p
        ));
    }, []);

    const reorderImages = useCallback((projectId, fromIndex, toIndex) => {
        setProjects(prev => prev.map(p => {
            if (p.id !== projectId) return p;
            const newImages = [...p.images];
            const [removed] = newImages.splice(fromIndex, 1);
            newImages.splice(toIndex, 0, removed);
            return { ...p, images: newImages };
        }));
    }, []);

    return (
        <PortfolioContext.Provider value={{
            projects,
            isLoading,
            error,
            storageMode,
            uploadImage,
            addProject,
            updateProject,
            deleteProject,
            reorderProject,
            addImageToProject,
            removeImageFromProject,
            reorderImages,
            resetToDefaults
        }}>
            {children}
        </PortfolioContext.Provider>
    );
};

export const usePortfolio = () => {
    const context = useContext(PortfolioContext);
    if (!context) {
        throw new Error('usePortfolio must be used within a PortfolioProvider');
    }
    return context;
};
